import { useCallback, useEffect, useRef, useState } from 'react';
import { Share2 } from 'lucide-react';

const SUCCESS_RESET_MS = 2400;

const isNativeShareCancel = (err) => {
  const name = String(err?.name || '').toLowerCase();
  const message = String(err?.message || '').toLowerCase();
  return name === 'aborterror' || message.includes('cancel');
};

const copyWithHiddenTextarea = (value) => {
  if (typeof document === 'undefined' || !document.body) {
    throw new Error('Copy fallback unavailable.');
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.setAttribute('aria-hidden', 'true');
  textarea.tabIndex = -1;
  textarea.style.position = 'fixed';
  textarea.style.top = '-1000px';
  textarea.style.left = '-1000px';
  textarea.style.width = '1px';
  textarea.style.height = '1px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);
  try {
    if (
      typeof document.execCommand === 'function' &&
      document.execCommand('copy')
    ) {
      return;
    }
    throw new Error('Copy command unavailable.');
  } finally {
    textarea.remove();
  }
};

const copyShareUrl = async (url) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
    return;
  }
  copyWithHiddenTextarea(url);
};

export default function RichShareButton({
  ariaLabel,
  className = '',
  copiedLabel = 'Link copied',
  label,
  text,
  title,
  url,
}) {
  const [statusLabel, setStatusLabel] = useState('');
  const [showUrl, setShowUrl] = useState(false);
  const resetTimerRef = useRef(null);

  const clearResetTimer = useCallback(() => {
    if (resetTimerRef.current) {
      window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  const showCopiedState = useCallback(() => {
    clearResetTimer();
    setStatusLabel(copiedLabel);
    setShowUrl(false);
    resetTimerRef.current = window.setTimeout(() => {
      setStatusLabel('');
      resetTimerRef.current = null;
    }, SUCCESS_RESET_MS);
  }, [clearResetTimer, copiedLabel]);

  useEffect(() => clearResetTimer, [clearResetTimer]);

  const handleShare = useCallback(async () => {
    if (!url) return;
    clearResetTimer();
    setStatusLabel('');
    setShowUrl(false);
    const payload = { title, text, url };
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share(payload);
        showCopiedState();
        return;
      } catch (err) {
        if (isNativeShareCancel(err)) return;
      }
    }
    try {
      await copyShareUrl(url);
      showCopiedState();
    } catch (err) {
      setStatusLabel('Copy link');
      setShowUrl(true);
    }
  }, [clearResetTimer, showCopiedState, text, title, url]);

  const visibleLabel = statusLabel || label;

  return (
    <div className={`hitlab-rich-share${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        className="hitlab-rich-share-button"
        onClick={handleShare}
        aria-label={ariaLabel || label}
      >
        <Share2 aria-hidden="true" size={16} />
        <span aria-live="polite">{visibleLabel}</span>
      </button>
      {showUrl ? (
        <p className="hitlab-rich-share-url" aria-live="polite">
          {url}
        </p>
      ) : null}
    </div>
  );
}
